/**
 * A seção "Finalizados": busca, ordenação e a tabela dos pedidos encerrados.
 *
 * Papel: mostra uma linha por pedido `finalizado` — entregue e confirmado, ou
 * reprovado —, com descrição, número, data, casa, fornecedor, categoria,
 * solicitante, aprovador, comprador, resultado e valor, sob o cabeçalho da
 * seção e o campo de busca.
 *
 * Como usar: quem monta passa **todos** os pedidos, e o filtro pelos
 * finalizados acontece aqui. O pedido reprovado aparece junto com os entregues,
 * distinguido pela coluna Resultado — os dois terminaram, e separá-los em duas
 * tabelas faria procurar um pedido antigo em dois lugares. Sem `onAbrir` e
 * `onReabrir`, a coluna de ações some, que é o que acontece no nível `ver`.
 *
 * A coluna Categoria é a `categoria` do pedido, pelo `ROTULOS_CATEGORIA`, e
 * não a `categoriaFinanceira` dos itens: esta não tem campo em tela nenhuma, e
 * a coluna sairia vazia para todo pedido criado aqui. Categoria e Solicitante
 * deverão continuar na tabela, porque o nível `ver` não abre o formulário e só
 * enxerga os dois aqui e no cartão do quadro.
 *
 * A busca e a ordenação vivem aqui, no estado do componente, e não na URL:
 * sair da tela e voltar começa de novo pela data mais recente e sem busca. A
 * busca procura no texto de todas as colunas de `COLUNAS` — inclusive a data e
 * o valor como aparecem na tela —, e a ordenação troca de sentido a cada
 * clique no mesmo cabeçalho.
 *
 * "⇩ Exportar Excel", no cabeçalho da seção, exporta as linhas que a tabela
 * está mostrando — com a busca aplicada e na ordem em vigor —, em qualquer
 * nível de acesso, pela `planilhaDosFinalizados`. Com a busca sem resultado, o
 * arquivo sai só com o cabeçalho. A falha em carregar a biblioteca de planilha
 * aparece embaixo do cabeçalho da seção, e some no clique seguinte.
 */
import { type PedidoCompra, totalDoPedido } from '@concierge/shared';
import { useState } from 'react';

import { formatarData, formatarDinheiro } from '@/shared/lib/formato';
import { exportarPlanilha } from '@/shared/lib/planilha';
import { hojeEmIso } from '@/shared/lib/vencimento';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { CabecalhoDeSecao } from '@/shared/ui/cabecalho-de-secao';
import { CampoDeBusca } from '@/shared/ui/campo-de-busca';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { Vazio } from '@/shared/ui/vazio';
import { planilhaDosFinalizados } from '../planilha-dos-finalizados';
import {
  descricaoDoPedido,
  fornecedorDoPedido,
  residenciaDoPedido,
  resultadoDoPedido,
} from '../resumo';
import { ROTULOS_CATEGORIA } from '../rotulos';

interface TabelaFinalizadosProps {
  pedidos: PedidoCompra[];
  /** Chamado com o pedido da linha em "ver detalhes"; ausente esconde as ações. */
  onAbrir?: (pedido: PedidoCompra) => void;
  /** Chamado com o pedido da linha em "reabrir"; ausente esconde as ações. */
  onReabrir?: (pedido: PedidoCompra) => void;
}

/** As colunas que ordenam, na ordem em que aparecem. */
type Coluna =
  | 'descricao'
  | 'numero'
  | 'data'
  | 'residencia'
  | 'fornecedor'
  | 'categoria'
  | 'solicitante'
  | 'aprovador'
  | 'compradoPor'
  | 'resultado'
  | 'valor';

/** Cabeçalho visível de cada coluna. */
const COLUNAS: readonly { coluna: Coluna; rotulo: string }[] = [
  { coluna: 'descricao', rotulo: 'Descrição' },
  { coluna: 'numero', rotulo: 'Número' },
  { coluna: 'data', rotulo: 'Data' },
  { coluna: 'residencia', rotulo: 'Residência' },
  { coluna: 'fornecedor', rotulo: 'Fornecedor' },
  { coluna: 'categoria', rotulo: 'Categoria' },
  { coluna: 'solicitante', rotulo: 'Solicitante' },
  { coluna: 'aprovador', rotulo: 'Aprovador' },
  { coluna: 'compradoPor', rotulo: 'Comprado por' },
  { coluna: 'resultado', rotulo: 'Resultado' },
  { coluna: 'valor', rotulo: 'Valor' },
];

/** A coluna e o sentido da ordenação em vigor. */
interface Ordem {
  coluna: Coluna;
  sentido: 'asc' | 'desc';
}

/**
 * O valor de uma coluna para ordenar.
 *
 * A data compara pela forma ISO, que ordena igual à cronologia, e o valor pelo
 * número; as demais comparam como texto. O resultado compara pela palavra que
 * a pílula mostra, e não pela presença do motivo, e a categoria pelo rótulo de
 * `ROTULOS_CATEGORIA`, e não pelo valor do enum — é o rótulo que a busca
 * precisa encontrar, e o valor do enum ordenaria por uma grafia que a tela não
 * mostra.
 *
 * @param pedido - O pedido da linha.
 * @param coluna - A coluna pela qual se ordena.
 * @returns O número do valor, ou o texto da célula.
 */
function valorParaOrdenar(pedido: PedidoCompra, coluna: Coluna): string | number {
  switch (coluna) {
    case 'descricao':
      return descricaoDoPedido(pedido);
    case 'numero':
      return pedido.numero;
    case 'data':
      return pedido.data;
    case 'residencia':
      return residenciaDoPedido(pedido);
    case 'fornecedor':
      return fornecedorDoPedido(pedido) ?? '';
    case 'categoria':
      return ROTULOS_CATEGORIA[pedido.categoria];
    case 'solicitante':
      return pedido.solicitante;
    case 'aprovador':
      return pedido.aprovador ?? '';
    case 'compradoPor':
      return pedido.compradoPor ?? '';
    case 'resultado':
      return resultadoDoPedido(pedido);
    case 'valor':
      return totalDoPedido(pedido.itens);
  }
}

/**
 * Se o pedido casa com o termo de busca.
 *
 * @param pedido - O pedido da linha.
 * @param termo - O que a pessoa digitou, já em minúsculas e sem espaços nas
 *   pontas.
 * @returns Verdadeiro quando o termo aparece no texto de alguma coluna.
 */
function casaComABusca(pedido: PedidoCompra, termo: string): boolean {
  const textos = COLUNAS.map(({ coluna }) => String(valorParaOrdenar(pedido, coluna)));
  textos.push(formatarData(pedido.data), formatarDinheiro(totalDoPedido(pedido.itens)));
  return textos.join(' ').toLowerCase().includes(termo);
}

/**
 * A seção de finalizados.
 *
 * @param pedidos - Todos os pedidos; só os finalizados entram.
 * @param onAbrir - Chamado em "ver detalhes"; ausente esconde as ações.
 * @param onReabrir - Chamado em "reabrir", que devolve o pedido à etapa
 *   anterior; ausente esconde as ações.
 * @returns O cabeçalho da seção, a busca e a tabela — ou o aviso de que nenhum
 *   pedido foi finalizado ainda.
 */
export function TabelaFinalizados({ pedidos, onAbrir, onReabrir }: TabelaFinalizadosProps) {
  const [busca, setBusca] = useState('');
  const [ordem, setOrdem] = useState<Ordem>({ coluna: 'data', sentido: 'desc' });
  const [gerandoPlanilha, setGerandoPlanilha] = useState(false);
  const [planilhaFalhou, setPlanilhaFalhou] = useState(false);

  const finalizados = pedidos.filter((pedido) => pedido.status === 'finalizado');
  const termo = busca.trim().toLowerCase();
  const fator = ordem.sentido === 'asc' ? 1 : -1;
  const mostrados = finalizados
    .filter((pedido) => termo === '' || casaComABusca(pedido, termo))
    .sort((um, outro) => {
      const a = valorParaOrdenar(um, ordem.coluna);
      const b = valorParaOrdenar(outro, ordem.coluna);
      if (typeof a === 'number' && typeof b === 'number') return (a - b) * fator;
      return String(a).localeCompare(String(b), 'pt-BR', { sensitivity: 'base' }) * fator;
    });
  const mostrarAcoes = onAbrir !== undefined || onReabrir !== undefined;

  /** Ordena pela coluna clicada, ou inverte o sentido se ela já é a da ordem. */
  function handleOrdenar(coluna: Coluna) {
    setOrdem((atual) =>
      atual.coluna === coluna
        ? { coluna, sentido: atual.sentido === 'asc' ? 'desc' : 'asc' }
        : { coluna, sentido: 'asc' },
    );
  }

  /** Exporta as linhas mostradas, com a data de hoje no nome do arquivo. */
  async function handleExportarExcel() {
    setGerandoPlanilha(true);
    setPlanilhaFalhou(false);
    try {
      await exportarPlanilha(planilhaDosFinalizados(mostrados, hojeEmIso()));
    } catch {
      setPlanilhaFalhou(true);
    } finally {
      setGerandoPlanilha(false);
    }
  }

  return (
    <section>
      <CabecalhoDeSecao
        titulo="Finalizados"
        acoes={
          <Button
            type="button"
            variant="outline"
            onClick={() => void handleExportarExcel()}
            disabled={gerandoPlanilha}
          >
            ⇩ Exportar Excel
          </Button>
        }
        className="mx-5 mt-6 mb-0 print:hidden"
      />

      {planilhaFalhou && (
        <p className="mx-5 mt-2 text-sm text-destructive print:hidden" role="alert">
          A planilha não pôde ser gerada. Verifique a conexão com a internet e tente novamente.
        </p>
      )}

      <div className="mx-5 mt-3 print:hidden [&>div]:mb-4">
        <CampoDeBusca
          aria-label="Pesquisar finalizados"
          placeholder="Pesquisar finalizados..."
          value={busca}
          onChange={(evento) => setBusca(evento.target.value)}
        />
      </div>

      {finalizados.length === 0 ? (
        <Vazio mensagem="Nenhum pedido finalizado ainda." className="mx-5" />
      ) : (
        <div className="mx-5">
          <Table>
            <TableHeader>
              <TableRow>
                {COLUNAS.map(({ coluna, rotulo }) => {
                  const ativa = ordem.coluna === coluna;
                  return (
                    <TableHead
                      key={coluna}
                      aria-sort={
                        ativa
                          ? ordem.sentido === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                    >
                      <button
                        type="button"
                        title={`Ordenar por ${rotulo}`}
                        onClick={() => handleOrdenar(coluna)}
                        className="cursor-pointer select-none"
                      >
                        {rotulo}
                        {ativa && (ordem.sentido === 'asc' ? ' ▲' : ' ▼')}
                      </button>
                    </TableHead>
                  );
                })}
                {mostrarAcoes && (
                  <TableHead>
                    <span className="sr-only">Ações</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {mostrados.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUNAS.length + (mostrarAcoes ? 1 : 0)}
                    className="p-5 text-center text-texto-fraco"
                  >
                    Nenhum resultado para a pesquisa.
                  </TableCell>
                </TableRow>
              ) : (
                mostrados.map((pedido) => (
                  <TableRow key={pedido.id}>
                    <TableCell className="text-texto-medio">
                      {descricaoDoPedido(pedido)}
                    </TableCell>
                    <TableCell>{pedido.numero === '' ? '—' : pedido.numero}</TableCell>
                    <TableCell>{formatarData(pedido.data)}</TableCell>
                    <TableCell className="text-texto-medio">
                      {residenciaDoPedido(pedido)}
                    </TableCell>
                    <TableCell className="text-texto-medio">
                      {fornecedorDoPedido(pedido) ?? '—'}
                    </TableCell>
                    <TableCell className="text-texto-medio">
                      {ROTULOS_CATEGORIA[pedido.categoria]}
                    </TableCell>
                    <TableCell className="text-texto-medio">
                      {pedido.solicitante === '' ? '—' : pedido.solicitante}
                    </TableCell>
                    <TableCell>{pedido.aprovador ?? '—'}</TableCell>
                    <TableCell>{pedido.compradoPor ?? '—'}</TableCell>
                    <TableCell>
                      {pedido.motivoReprovacao === null ? (
                        <Badge variant="concluido">Concluído</Badge>
                      ) : (
                        <Badge variant="vencido" title={pedido.motivoReprovacao}>
                          Reprovado
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{formatarDinheiro(totalDoPedido(pedido.itens))}</TableCell>
                    {mostrarAcoes && (
                      <TableCell>
                        <div className="flex gap-2">
                          {onAbrir !== undefined && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onAbrir(pedido)}
                            >
                              ver detalhes
                            </Button>
                          )}
                          {onReabrir !== undefined && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => onReabrir(pedido)}
                            >
                              reabrir
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
